import { TableCell, TableRow, Box, Typography, Chip, Collapse } from "@mui/material";
import { AreaByScopeOfWork } from "../../../types/scope-of-work.interface";

interface AreaRowProps {
  area: AreaByScopeOfWork;
  areaTime?: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

export const AreaRow = ({ area, areaTime, isExpanded, onToggleExpand }: AreaRowProps) => {
  const hasTasks = area.tasks && area.tasks.length > 0;
  const visibleTasks = hasTasks ? area.tasks.slice(0, 2) : [];
  const hiddenTasks = hasTasks && area.tasks.length > 2 ? area.tasks.slice(2) : [];

  return (
    <TableRow sx={{ "&:not(:last-child)": { borderBottom: "1px solid", borderColor: "divider" } }}>
      <TableCell colSpan={5} sx={{ px: 2, py: 2, maxWidth: "100%", overflow: "hidden" }}>
        <Box display="flex" alignItems="flex-start" gap={1} sx={{ width: "100%" }}>
          <Box flex={1} sx={{ minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
            <Box display="flex" alignItems="center" gap={1} mb={hasTasks ? 1 : 0.5} sx={{ flexWrap: "wrap" }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "medium",
                  color: "text.primary",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  maxWidth: "100%",
                }}
              >
                {area.area?.name || "Unknown Area"}
              </Typography>
              {areaTime && (
                <Chip
                  label={areaTime}
                  size="small"
                  sx={{
                    fontSize: "0.625rem",
                    height: 20,
                    bgcolor: "grey.100",
                    color: "text.secondary",
                    fontWeight: 500,
                  }}
                />
              )}
            </Box>
            {hasTasks && (
              <Box sx={{ mt: 0.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.75,
                  }}
                >
                  {visibleTasks.map((task) => (
                    <Chip
                      key={task.uid}
                      label={task.name}
                      variant="outlined"
                      size="small"
                      sx={{
                        fontSize: "0.6875rem",
                        height: 24,
                        borderColor: "divider",
                        color: "text.secondary",
                        "&:hover": {
                          borderColor: "primary.main",
                          color: "primary.main",
                          bgcolor: "action.hover",
                        },
                      }}
                    />
                  ))}
                  {hiddenTasks.length > 0 && !isExpanded && (
                    <Chip
                      label={`${hiddenTasks.length} more...`}
                      variant="outlined"
                      size="small"
                      onClick={onToggleExpand}
                      sx={{
                        fontSize: "0.6875rem",
                        height: 24,
                        borderColor: "primary.main",
                        color: "primary.main",
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: "primary.light",
                          opacity: 0.8,
                        },
                      }}
                    />
                  )}
                </Box>
                {hiddenTasks.length > 0 && (
                  <Box sx={{ mt: 0.75 }}>
                    <Typography
                      onClick={onToggleExpand}
                      variant="caption"
                      sx={{
                        fontSize: "0.6875rem",
                        color: "primary.main",
                        fontWeight: 500,
                        cursor: "pointer",
                        display: "inline-block",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {isExpanded ? "Hide All Tasks" : "Show All Tasks"}
                    </Typography>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.75,
                          mt: 0.75,
                        }}
                      >
                        {hiddenTasks.map((task) => (
                          <Chip
                            key={task.uid}
                            label={task.name}
                            variant="outlined"
                            size="small"
                            sx={{
                              fontSize: "0.6875rem",
                              height: 24,
                              borderColor: "divider",
                              color: "text.secondary",
                              "&:hover": {
                                borderColor: "primary.main",
                                color: "primary.main",
                                bgcolor: "action.hover",
                              },
                            }}
                          />
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
                )}
              </Box>
            )}
            {!hasTasks && area.area?.description && (
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.6875rem",
                  mt: 0.5,
                  fontStyle: "italic",
                  display: "block",
                }}
              >
                {area.area.description}
              </Typography>
            )}
          </Box>
        </Box>
      </TableCell>
    </TableRow>
  );
};
